import { useEffect, useId, useState, type FormEvent } from 'react';
import { Banner, Button, Dialog, TextField } from '../../../ds/components';
import { ApiError, errorMessage } from '../../../lib/api';
import { saveRequestNote, type FinancierRecordFields } from './api';
import { addDaysKey, todayKey } from './form';
import styles from './financier.module.css';

/**
 * Finanzierer 05 „Extern angefragt" (`PUT /financings/:id/request-note`). The first note moves the stage to
 * „Angefragt"; the same dialog edits the note („Vermerk bearbeiten", FZ-06) and records a further bank (FZ-08).
 */

export interface RequestNoteDialogProps {
  open: boolean;
  financing: FinancierRecordFields;
  onClose: () => void;
  onDone: () => void;
}

type Errors = Partial<Record<'requestedOn' | 'offersExpectedOn' | 'where' | 'note', string>>;

export function RequestNoteDialog({ open, financing, onClose, onDone }: RequestNoteDialogProps) {
  const formId = useId();
  const existing = financing.request;
  const [requestedOn, setRequestedOn] = useState('');
  const [offersExpectedOn, setOffersExpectedOn] = useState('');
  const [where, setWhere] = useState('');
  const [note, setNote] = useState('');
  const [errors, setErrors] = useState<Errors>({});
  const [banner, setBanner] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  useEffect(() => {
    if (!open) return;
    const today = todayKey();
    setRequestedOn(existing?.requestedOn ?? today);
    setOffersExpectedOn(existing?.offersExpectedOn ?? addDaysKey(today, 7));
    setWhere(existing?.where ?? '');
    setNote(existing?.note ?? '');
    setErrors({});
    setBanner(null);
  }, [open]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (pending) return;
    const next: Errors = {};
    if (!requestedOn) next.requestedOn = 'Pflichtangabe.';
    if (!offersExpectedOn) next.offersExpectedOn = 'Pflichtangabe.';
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    setPending(true);
    setBanner(null);
    try {
      await saveRequestNote(financing.id, {
        requestedOn,
        offersExpectedOn,
        where: where.trim() === '' ? null : where.trim(),
        note: note.trim() === '' ? null : note.trim(),
      });
      onDone();
    } catch (error) {
      if (error instanceof ApiError && error.status === 400 && Object.keys(error.fields).length > 0) {
        setErrors(error.fields);
      } else if (error instanceof ApiError && error.status === 409) {
        const missing = error.details?.['missing'];
        setBanner(
          Array.isArray(missing) && missing.length > 0
            ? `${error.message} Es fehlen: ${missing.filter((item) => typeof item === 'string').join(', ')}.`
            : error.message,
        );
      } else {
        setBanner(errorMessage(error));
      }
    } finally {
      setPending(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      width={560}
      overlayTop={60}
      eyebrow={`${financing.numberLabel} · ${financing.linked.contact.name}`}
      title="Extern angefragt"
      subtitle="DAMOKLES sendet nichts an Banken. Vermerken Sie, dass Sie die Finanzierung außerhalb angefragt haben. Die Angebote laden Sie danach hoch."
      footerNote={existing ? undefined : 'Stufe wechselt auf „Angefragt“'}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={pending}>
            Abbrechen
          </Button>
          <Button type="submit" form={formId} loading={pending}>
            Vermerken
          </Button>
        </>
      }
    >
      <form id={formId} className={styles.stack} onSubmit={submit} noValidate>
        {banner ? <Banner tone="danger">{banner}</Banner> : null}
        <div className={`${styles.dialogGrid} ${styles.dialogGridWide}`}>
          <TextField
            label="Angefragt am"
            type="date"
            value={requestedOn}
            max={todayKey()}
            onChange={(event) => setRequestedOn(event.target.value)}
            error={errors.requestedOn}
          />
          <TextField
            label="Angebote erwartet bis"
            type="date"
            value={offersExpectedOn}
            min={requestedOn || undefined}
            onChange={(event) => setOffersExpectedOn(event.target.value)}
            hint="Erzeugt eine Task „Angebote nachfassen“"
            error={errors.offersExpectedOn}
          />
        </div>
        <TextField
          label="Wo angefragt"
          optional
          placeholder="z. B. Plattform oder Banken direkt"
          value={where}
          maxLength={200}
          onChange={(event) => setWhere(event.target.value)}
          error={errors.where}
        />
        <TextField
          label="Notiz"
          optional
          value={note}
          maxLength={2000}
          onChange={(event) => setNote(event.target.value)}
          error={errors.note}
        />
      </form>
    </Dialog>
  );
}
